import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  PlusCircle, TrendingUp, TrendingDown, AlertTriangle, Wallet,
  Briefcase, RefreshCw, Loader2, ArrowUpRight, ArrowDownRight,
  BarChart3, Layers, Eye, ChevronRight, Sparkles,
} from 'lucide-react';
import { usePortfolioStore } from '../../../state/portfolioStore';
import SectionNav from '../../../components/layout/SectionNav';
import { portfolioTabs } from '../../../nav/publicNav';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];

const fmt = (v) => {
  if (v == null || isNaN(v)) return '₹0';
  const abs = Math.abs(v);
  if (abs >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export default function PortfolioDashboardPage() {
  const navigate = useNavigate();
  const holdings = usePortfolioStore((s) => s.holdings);
  const overview = usePortfolioStore((s) => s.overview);
  const overviewLoading = usePortfolioStore((s) => s.overviewLoading);
  const fetchOverview = usePortfolioStore((s) => s.fetchOverview);
  const fetchHoldings = usePortfolioStore((s) => s.fetchHoldings);
  const seedSample = usePortfolioStore((s) => s.seedSample);
  const isLoading = usePortfolioStore((s) => s.isLoading);
  const performance = usePortfolioStore((s) => s.performance);
  const fetchPerformance = usePortfolioStore((s) => s.fetchPerformance);

  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => { fetchHoldings(); }, [fetchHoldings]);

  useEffect(() => {
    if (holdings.length > 0) {
      fetchOverview();
      fetchPerformance('1M');
    }
  }, [holdings.length, fetchOverview, fetchPerformance]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchOverview();
    setRefreshing(false);
  }, [fetchOverview]);

  const handleSeed = async () => {
    setSeeding(true);
    await seedSample();
    setSeeding(false);
  };

  const o = overview;

  const showSeedOption = !isLoading && !overviewLoading && holdings.length < 10;

  // ── Empty State ─────────────────────────────────────────────
  if (!isLoading && !overviewLoading && holdings.length === 0) {
    return (
      <div className="space-y-4">
        <SectionNav tabs={portfolioTabs} />
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 flex items-center justify-center mb-6">
            <Briefcase className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Your Portfolio</h2>
          <p className="text-muted-foreground max-w-md mb-8">
            Track your investments with real-time prices, performance analytics, and AI-powered insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 px-6 h-11"
              onClick={() => navigate('/public/portfolio/add-holding')}
            >
              <PlusCircle className="w-4 h-4" /> Add Your Holdings
            </Button>
            <Button
              variant="outline"
              className="gap-2 h-11 px-6 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950"
              onClick={handleSeed}
              disabled={seeding}
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Load Sample Portfolio (20 stocks)
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Or <button onClick={() => navigate('/public/portfolio/broker')} className="text-emerald-600 hover:underline">connect your broker</button> to import automatically
          </p>
        </div>
      </div>
    );
  }

  // ── Loading State ───────────────────────────────────────────
  if (overviewLoading && !o) {
    return (
      <div className="space-y-4">
        <SectionNav tabs={portfolioTabs} />
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-2 border-emerald-500/30 border-t-emerald-500 animate-spin" />
          </div>
          <p className="text-sm text-muted-foreground">Fetching live prices for {holdings.length} stocks...</p>
        </div>
      </div>
    );
  }

  const totalValue = o?.totalValue || 0;
  const totalInvested = o?.totalInvested || 0;
  const totalPnL = o?.totalPnL || 0;
  const totalPnLPct = o?.totalPnLPercent || 0;
  const dayChange = o?.dayChange || 0;
  const dayChangePct = o?.dayChangePercent || 0;
  const sectorAllocation = o?.sectorAllocation || [];
  const topHoldings = o?.topHoldings || [];
  const riskFlags = o?.riskFlags || [];
  const allHoldings = o?.holdings || holdings;
  const miniChart = performance?.timeseries?.slice(-30) || [];

  // separate gainers/losers
  const sorted = [...allHoldings].sort((a, b) => (b.dayChangePercent || 0) - (a.dayChangePercent || 0));
  const topGainers = sorted.filter((h) => (h.dayChangePercent || 0) > 0).slice(0, 3);
  const topLosers = sorted.filter((h) => (h.dayChangePercent || 0) < 0).reverse().slice(0, 3);

  return (
    <div className="space-y-5">
      <SectionNav tabs={portfolioTabs} />

      {/* ── Header Row ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1">Portfolio Value</p>
          <div className="flex items-baseline gap-3">
            <h1 className="text-4xl font-bold tracking-tight">{fmt(totalValue)}</h1>
            <div className={`flex items-center gap-1 text-sm font-semibold ${dayChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {dayChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {fmt(Math.abs(dayChange))} ({dayChangePct >= 0 ? '+' : ''}{dayChangePct.toFixed(2)}%) today
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {allHoldings.length} holdings · Invested {fmt(totalInvested)}
            {o?.lastUpdated && ` · ${new Date(o.lastUpdated).toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" className="text-xs gap-1.5 h-8" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs h-8" onClick={() => navigate('/public/portfolio/add-holding')}>
            <PlusCircle className="w-3.5 h-3.5" /> Add
          </Button>
        </div>
      </div>

      {/* ── P&L Summary Cards ──────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-border/40 bg-gradient-to-br from-background to-secondary/20">
          <CardContent className="p-4">
            <p className="text-[11px] text-muted-foreground font-medium mb-1">Total P&L</p>
            <p className={`text-xl font-bold ${totalPnL >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {totalPnL >= 0 ? '+' : ''}{fmt(totalPnL)}
            </p>
            <p className={`text-xs font-medium ${totalPnLPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {totalPnLPct >= 0 ? '+' : ''}{totalPnLPct.toFixed(2)}% all time
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-gradient-to-br from-background to-secondary/20">
          <CardContent className="p-4">
            <p className="text-[11px] text-muted-foreground font-medium mb-1">Today's Change</p>
            <p className={`text-xl font-bold ${dayChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {dayChange >= 0 ? '+' : ''}{fmt(dayChange)}
            </p>
            <p className={`text-xs font-medium ${dayChangePct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {dayChangePct >= 0 ? '+' : ''}{dayChangePct.toFixed(2)}%
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-gradient-to-br from-background to-secondary/20">
          <CardContent className="p-4">
            <p className="text-[11px] text-muted-foreground font-medium mb-1">Invested</p>
            <p className="text-xl font-bold">{fmt(totalInvested)}</p>
            <p className="text-xs text-muted-foreground">{allHoldings.length} stocks</p>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-gradient-to-br from-background to-secondary/20">
          <CardContent className="p-4">
            <p className="text-[11px] text-muted-foreground font-medium mb-1">Sectors</p>
            <p className="text-xl font-bold">{sectorAllocation.length}</p>
            <p className="text-xs text-muted-foreground">diversified across</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Empty portfolio onboarding banner ─────────────────────── */}
      {showSeedOption && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500 shrink-0" />
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Your portfolio is empty.</span> Connect a broker for live sync, or add holdings manually.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="text-xs gap-1.5 h-7"
              onClick={() => navigate('/public/portfolio/broker')}>
              Connect broker
            </Button>
            <Button size="sm" className="text-xs gap-1.5 h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => navigate('/public/portfolio/add-holding')}>
              Add a holding
            </Button>
          </div>
        </div>
      )}

      {/* ── Risk Flags ─────────────────────────────────────────── */}
      {riskFlags.length > 0 && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl border border-amber-500/20 bg-amber-500/5">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-xs">
            <span className="font-semibold text-foreground">Risk Alert: </span>
            {riskFlags.map((f, i) => <span key={i} className="text-muted-foreground">{f}{i < riskFlags.length - 1 ? ' · ' : ''}</span>)}
          </div>
        </div>
      )}

      {/* ── Chart + Allocation Row ─────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Portfolio Value Chart */}
        <Card className="lg:col-span-3 border-border/40">
          <CardHeader className="pb-0 flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold">Portfolio Value</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs text-emerald-600 h-6" onClick={() => navigate('/public/portfolio/performance')}>
              Full Chart <ChevronRight className="w-3 h-3 ml-0.5" />
            </Button>
          </CardHeader>
          <CardContent className="pt-2">
            {miniChart.length > 1 ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={miniChart}>
                    <defs>
                      <linearGradient id="pGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/20" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5, 10)} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={50} />
                    <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, 'Portfolio']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Area type="monotone" dataKey="value" stroke="#10b981" fill="url(#pGrad)" strokeWidth={2} dot={false} />
                    {miniChart[0]?.benchmark && (
                      <Area type="monotone" dataKey="benchmark" stroke="#6366f1" fill="none" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-52 flex items-center justify-center">
                <p className="text-xs text-muted-foreground">Chart data loading in background...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sector Donut */}
        <Card className="lg:col-span-2 border-border/40">
          <CardHeader className="pb-0 flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold">Allocation</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs text-emerald-600 h-6" onClick={() => navigate('/public/portfolio/analytics')}>
              Details <ChevronRight className="w-3 h-3 ml-0.5" />
            </Button>
          </CardHeader>
          <CardContent className="pt-2">
            {sectorAllocation.length > 0 ? (
              <>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={sectorAllocation} dataKey="weight" nameKey="sector" cx="50%" cy="50%" innerRadius={38} outerRadius={62} paddingAngle={2} strokeWidth={0}>
                        {sectorAllocation.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1">
                  {sectorAllocation.slice(0, 8).map((sa, i) => (
                    <div key={sa.sector} className="flex items-center gap-1.5 text-[10px] text-muted-foreground truncate">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="truncate">{sa.sector}</span>
                      <span className="ml-auto font-medium text-foreground">{sa.weight.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-52 flex items-center justify-center text-xs text-muted-foreground">No allocation data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Day Movers ─────────────────────────────────────────── */}
      {(topGainers.length > 0 || topLosers.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {topGainers.length > 0 && (
            <Card className="border-border/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold text-emerald-600 flex items-center gap-1.5 uppercase tracking-wide">
                  <TrendingUp className="w-3.5 h-3.5" /> Today's Gainers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {topGainers.map((h) => (
                  <button key={h.symbol} onClick={() => navigate(`/public/portfolio/stock/${h.symbol}`)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-secondary/40 transition-colors text-left border-b border-border/20 last:border-0">
                    <div>
                      <p className="text-sm font-medium">{h.company_name || h.symbol}</p>
                      <span className="text-[10px] font-mono text-muted-foreground">{h.symbol}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-600">+{(h.dayChangePercent || 0).toFixed(2)}%</p>
                      <p className="text-[10px] text-muted-foreground">₹{(h.currentPrice || 0).toFixed(0)}</p>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
          {topLosers.length > 0 && (
            <Card className="border-border/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold text-red-500 flex items-center gap-1.5 uppercase tracking-wide">
                  <TrendingDown className="w-3.5 h-3.5" /> Today's Losers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {topLosers.map((h) => (
                  <button key={h.symbol} onClick={() => navigate(`/public/portfolio/stock/${h.symbol}`)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-secondary/40 transition-colors text-left border-b border-border/20 last:border-0">
                    <div>
                      <p className="text-sm font-medium">{h.company_name || h.symbol}</p>
                      <span className="text-[10px] font-mono text-muted-foreground">{h.symbol}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-red-500">{(h.dayChangePercent || 0).toFixed(2)}%</p>
                      <p className="text-[10px] text-muted-foreground">₹{(h.currentPrice || 0).toFixed(0)}</p>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ── All Holdings ───────────────────────────────────────── */}
      <Card className="border-border/40">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">All Holdings ({allHoldings.length})</CardTitle>
          <Button variant="ghost" size="sm" className="text-xs text-emerald-600 h-6" onClick={() => navigate('/public/portfolio/holdings')}>
            Table View <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {/* Header */}
          <div className="hidden md:grid grid-cols-[2.5fr_1fr_1fr_1fr_1fr_0.7fr] gap-2 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border/30">
            <span>Company</span>
            <span className="text-right">Price</span>
            <span className="text-right">Day</span>
            <span className="text-right">Value</span>
            <span className="text-right">P&L</span>
            <span className="text-right">Wt</span>
          </div>
          <div className="divide-y divide-border/20 max-h-[480px] overflow-y-auto">
            {allHoldings.map((h) => {
              const pnlPct = h.unrealizedPnLPercent || 0;
              const dayPct = h.dayChangePercent || 0;
              const cp = h.currentPrice || h.avg_buy_price || 0;
              return (
                <button
                  key={h.id || h.symbol}
                  className="w-full text-left hover:bg-secondary/30 transition-colors"
                  onClick={() => navigate(`/public/portfolio/stock/${h.symbol}`)}
                >
                  {/* Desktop */}
                  <div className="hidden md:grid grid-cols-[2.5fr_1fr_1fr_1fr_1fr_0.7fr] gap-2 items-center px-4 py-2.5">
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                        {h.symbol?.slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{h.company_name || h.symbol}</p>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono text-muted-foreground">{h.symbol}</span>
                          {h.sector && <span className="text-[9px] text-muted-foreground bg-secondary/60 px-1.5 py-0.5 rounded">{h.sector}</span>}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-right font-medium">₹{cp.toFixed(0)}</p>
                    <p className={`text-sm text-right font-medium ${dayPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {dayPct >= 0 ? '+' : ''}{dayPct.toFixed(2)}%
                    </p>
                    <p className="text-sm text-right font-semibold">{fmt(h.currentValue)}</p>
                    <p className={`text-sm text-right font-semibold ${pnlPct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
                    </p>
                    <p className="text-xs text-right text-muted-foreground">{(h.weight || 0).toFixed(1)}%</p>
                  </div>
                  {/* Mobile */}
                  <div className="md:hidden flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center text-[10px] font-bold text-muted-foreground shrink-0">
                        {h.symbol?.slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{h.company_name || h.symbol}</p>
                        <p className="text-[10px] text-muted-foreground">{h.symbol} · {h.quantity} shares</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-sm font-semibold">{fmt(h.currentValue)}</p>
                      <p className={`text-[11px] font-medium ${pnlPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
